import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Picker } from '../../../ui/Picker';
import { askQuestion } from '../../../lib/backOfficeApi';
import { useEventSearch } from '../../../lib/queries/events';

/**
 * THE PARTS OF A QUESTION THAT EVERY SURFACE WRITES THE SAME WAY — paths through the vocabulary, what a path is,
 * and one condition's editor (its real values offered by asking for them). Ask composes questions with these;
 * the export dialog's "only include" is the same condition, so a filter reads the same wherever it is written.
 */
// Names are the database's (`amountPaid`, `net_amount`): both spellings read as words.
export const human = (s) => String(s || '').replace(/([a-z])([A-Z])/g, (_m, a, b) => `${a} ${b.toLowerCase()}`).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
export const OPS = [
  { value: 'eq', label: 'is' }, { value: 'ne', label: 'is not' }, { value: 'in', label: 'is one of' }, { value: 'like', label: 'contains' },
  { value: 'gt', label: '>' }, { value: 'gte', label: '≥' }, { value: 'lt', label: '<' }, { value: 'lte', label: '≤' },
  { value: 'null', label: 'is empty' }, { value: 'notnull', label: 'is set' },
];
/** A one-to-many path as a condition: the row has some of them, or none. */
export const MANY_OPS = [{ value: 'notnull', label: 'has some' }, { value: 'null', label: 'has none' }];
/** What a path from a table is: a column's kind, 'table' for a link, 'many' for its one-to-many children. */
export function kindAt(vocab, from, path) {
  const T = vocab?.tables || {};
  if (!path) return null;
  if (T[from]?.many?.[path]) return 'many';
  let t = from; const parts = path.split('.');
  for (let i = 0; i < parts.length; i++) {
    const tt = T[t]; if (!tt) return null;
    if (tt.columns[parts[i]] && i === parts.length - 1) return tt.columns[parts[i]].kind;
    if (tt.links?.[parts[i]]) { t = tt.links[parts[i]]; continue; }
    return null;
  }
  return 'table';
}
export const newCond = (vocab, from, path) => (kindAt(vocab, from, path) === 'many' ? { path, op: 'notnull' } : { path, op: 'eq', value: '' });

/** Every path reachable from a table: its columns, and its links' columns two hops out (and, for conditions,
 *  its own one-to-many children — "has some / has none"). */
export function pathsOf(vocab, from, { onlyKinds, includeTables = false, includeMany = false } = {}) {
  const out = [];
  const T = vocab?.tables || {};
  if (includeMany) for (const [many, child] of Object.entries(T[from]?.many || {})) out.push({ value: many, label: human(many), group: `Has ${T[child]?.label || child}s`, hint: 'has some / has none' });
  const walk = (table, prefix, group, depth) => {
    const t = T[table]; if (!t) return;
    for (const [k, c] of Object.entries(t.columns)) {
      if (onlyKinds && !onlyKinds.has(c.kind)) continue;
      out.push({ value: prefix ? `${prefix}.${k}` : k, label: c.label || human(k), group, hint: [c.kind, c.note].filter(Boolean).join(' · ') });
    }
    if (depth >= 2) return;
    for (const [link, to] of Object.entries(t.links || {})) {
      const p = prefix ? `${prefix}.${link}` : link;
      if (includeTables) out.push({ value: p, label: `${human(link)} (${T[to]?.label || to})`, group, hint: 'the whole record — shown by its name' });
      walk(to, p, `${group} › ${human(link)}`, depth + 1);
    }
  };
  walk(from, '', human(T[from]?.label || from), 0);
  return out;
}
export const isEventPath = (p) => /(^|\.)event$/.test(p || '');

/**
 * AN EVENT IS FOUND BY NAME, AMONG EVERY EVENT. A record's list is the busiest ones in the window (300 at most),
 * which is what is offered before anything is typed; a typed name also searches every event, any date — the
 * events search the page's own event filter uses — so a quiet or long-past event is reachable too.
 * `onSearch` goes on the Picker; `add` puts the matches after the counted ones.
 */
export function useEventFinder(enabled) {
  const [q, setQ] = useState('');
  const found = useEventSearch({ query: q, enabled });
  const add = (list) => {
    if (!enabled || q.trim().length < 2) return list;
    const have = new Set(list.map((o) => o.value));
    return [...list, ...(found.data?.rows || []).filter((e) => !have.has(String(e.id)))
      .map((e) => ({ value: String(e.id), label: e.name, hint: e.date || undefined }))];
  };
  return { onSearch: enabled ? setQ : undefined, add, searching: enabled && q.trim().length >= 2 && found.isFetching };
}
export const IconX = ({ onClick }) => <button type="button" onClick={onClick} className="text-[#9CA3AF] hover:text-[#1F2937] px-0.5" aria-label="Remove"><X className="w-3.5 h-3.5" /></button>;

export function Condition({ cond, paths, kind, question, onChange, onRemove }) {
  const [values, setValues] = useState(null);
  // A RECORD (an event, an organiser) is chosen by its name, never typed as an id: the ones that occur in this
  // question's window, busiest first, each held by its key.
  const isRecord = kind === 'table';
  const events = useEventFinder(isRecord && isEventPath(cond.path));
  // Real values to pick from — a question too: this column (or record), counted, in the same window.
  const loadValues = () => {
    if (values || !(isRecord || ['category', 'text', 'flag'].includes(kind))) return;
    askQuestion({ from: question.from, window: question.window, by: [cond.path], measures: [{ as: 'n', agg: 'count' }], sort: [{ by: 'n', dir: 'desc' }], limit: isRecord ? 300 : 50, tz: question.tz })
      .then((a) => {
        const k = a.columns.findIndex((c) => c.name.endsWith('__key')); const n = a.columns.findIndex((c) => c.name === 'n');
        setValues(a.rows.filter((r) => !isRecord || r[k] != null).map((r) => ({ value: String(isRecord ? r[k] : r[0]), label: String(r[0] ?? '— (none)'), hint: `${Number(r[n]).toLocaleString()} rows` })));
      })
      .catch(() => setValues([]));
  };
  // A record already chosen (a saved report's) is shown by its name, so its list is read at once.
  useEffect(() => { if (isRecord && cond.value) loadValues(); }, [isRecord, question.from]); // eslint-disable-line react-hooks/exhaustive-deps
  const needsValue = !['null', 'notnull'].includes(cond.op);
  const picks = kind === 'category' || kind === 'flag' || (isRecord && (cond.op === 'eq' || cond.op === 'ne'));
  return (
    <span className="inline-flex items-center gap-1 bg-[#FAFAF9] border border-[#E6E4E1] rounded-[8px] pl-1 pr-1 py-0.5">
      <Picker size="sm" variant="ghost" searchable value={cond.path} options={paths} onChange={(path) => onChange({ ...cond, path })} />
      <Picker size="sm" variant="ghost" value={cond.op} options={kind === 'many' ? MANY_OPS : OPS} onChange={(op) => onChange({ ...cond, op })} />
      {needsValue && (picks
        ? <Picker size="sm" variant="ghost" searchable value={String(cond.value ?? '')} placeholder={isRecord ? 'choose…' : 'value…'} onOpen={loadValues} loading={values === null || events.searching} onSearch={events.onSearch} options={events.add(values || [])}
            onChange={(value) => {
              // An event found by search joins the list, so it keeps showing by its name once the search is cleared.
              const found = events.add(values || []).find((o) => o.value === value);
              if (found && !(values || []).some((o) => o.value === value)) setValues((v) => [found, ...(v || [])]);
              onChange({ ...cond, value: kind === 'flag' ? value === 'true' : value });
            }} />
        : <input className="h-6 w-40 px-1.5 border border-[#E6E4E1] rounded-[5px] text-[12px]" placeholder={kind === 'table' ? 'id' : 'value'} value={Array.isArray(cond.value) ? cond.value.join(',') : (cond.value ?? '')}
            onChange={(e) => onChange({ ...cond, value: cond.op === 'in' ? e.target.value.split(',').map((x) => x.trim()).filter(Boolean) : e.target.value })} />)}
      <IconX onClick={onRemove} />
    </span>
  );
}


/** A path as a person names it: its column's label, else its last word ("order.event" → "Event"). */
export const labelOf = (vocab, from, path) => {
  let t = from; let col = null;
  for (const part of String(path).split('.')) { const tt = vocab?.tables?.[t]; if (!tt) break; if (tt.links?.[part]) { t = tt.links[part]; col = null; continue; } col = tt.columns?.[part] || null; }
  return col?.label || human(String(path).split('.').pop());
};
const OP_WORDS = { eq: 'is', ne: 'is not', in: 'is one of', notin: 'is none of', like: 'contains', gt: '>', gte: '≥', lt: '<', lte: '≤', null: 'is empty', notnull: 'is set' };
/** What a question counts, in a person's words: "Amount > 0 · State is successful · dated by Paid at". */
export function describeConditions(vocab, q, { skip = [] } = {}) {
  const parts = (q?.where || []).filter((c) => !skip.includes(c) && (c.op === 'null' || c.op === 'notnull' || c.value != null))
    .map((c) => `${labelOf(vocab, q.from, c.path)} ${OP_WORDS[c.op] || c.op}${c.op === 'null' || c.op === 'notnull' ? '' : ` ${Array.isArray(c.value) ? c.value.join(', ') : c.value}`}`);
  const time = q?.window?.path || vocab?.tables?.[q?.from]?.time;
  return [...parts, ...(time ? [`dated by ${labelOf(vocab, q.from, time).toLowerCase()}`] : [])].join(' · ');
}

/** A time bucket's value as the window it covers — wall times, in the question's zone — and the next finer bucket. */
const FINER = { year: 'month', month: 'day', week: 'day', day: 'hour', hour: 'minute' };
function bucketWindow(bucket, value) {
  const [y, mo = 1, d = 1, h = 0, mi = 0] = String(value).match(/\d+/g).map(Number);
  const iso = (t, withTime) => new Date(t).toISOString().slice(0, withTime ? 16 : 10);
  const start = Date.UTC(y, mo - 1, d, h, mi);
  if (bucket === 'hour' || bucket === 'minute') return { from: iso(start, true), to: iso(start + (bucket === 'hour' ? 3600e3 : 60e3), true) };
  // A bare date "to" is the whole of that day.
  const last = bucket === 'day' ? start : bucket === 'week' ? start + 6 * 864e5 : bucket === 'month' ? Date.UTC(y, mo, 0) : Date.UTC(y + 1, 0, 0);
  return { from: iso(start), to: iso(last) };
}
const nameOfDim = (d) => (typeof d === 'string' ? d.replace(/\./g, '_') : d.as || (d.bucket ? `${d.path.replace(/\./g, '_')}_${d.bucket}` : d.path.replace(/\./g, '_')));

/**
 * A value in the answer is a door: the question narrowed to it. `col` is the answer column, `value` its value
 * there, `key` the id behind a record's name. Composes — a grid cell is its row and its column.
 */
export const narrow = (q, { col, value, key }) => {
  if (col.name === 'currency') return { ...q, currency: value };
  const by = q.by || [];
  const d = by.find((x) => nameOfDim(x) === col.name);
  if (!d) return q;
  const path = typeof d === 'string' ? d : d.path;
  if (typeof d === 'object' && d.bucket) {
    // A time bucket narrows the WINDOW to it, and the breakdown one step finer (a minute is as fine as it goes).
    if (!(d.bucket in FINER || d.bucket === 'minute') || !value) return q;
    return { ...q, window: { path: d.path, ...bucketWindow(d.bucket, value) }, by: by.map((x) => (x === d ? { ...d, bucket: FINER[d.bucket] || 'minute' } : x)) };
  }
  const v = key !== undefined ? key : value;
  const rest = by.filter((x) => x !== d);
  // With no breakdown left there are no groups, so "only groups where…" would hide the total invisibly.
  return { ...q, where: [...(q.where || []), v == null ? { path, op: 'null' } : { path, op: 'eq', value: v }], by: rest, ...(rest.length ? {} : { having: [] }) };
};
